/**
 * CustomerList Component
 * Professional SaaS-style table layout with stats dashboard and sortable columns
 * Refactored for modularity - all files under 500 lines
 */

import React from 'react';
import { Container, Box, Typography, Snackbar, Alert, Menu, MenuItem } from '@mui/material';
import type { Customer } from '../../types/customer';
import { CustomerStats } from './CustomerStats';
import { CustomerModal } from './CustomerModal';
import { CustomerListHeader } from './components/CustomerListHeader';
import { CustomerTable } from './components/CustomerTable';
import { CustomerPagination } from './components/CustomerPagination';
import { DeleteCustomerDialog } from './components/DeleteCustomerDialog';
import { useCustomerList } from './hooks/useCustomerList';
import { useCustomerTable } from './hooks/useCustomerTable';

export const CustomerList: React.FC = () => {
  // Custom hooks for state management and business logic
  const {
    customers,
    total,
    totalPages,
    page,
    search,
    isLoading,
    isError,
    error,
    isCreating,
    isUpdating,
    isDeleting,
    setPage,
    setSearch,
    setSort,
    selectedCustomer,
    isModalOpen,
    deleteModalOpen,
    customerToDelete,
    snackbar,
    handleOpenAddModal,
    handleCloseModal,
    handleSubmit,
    handleRowClick,
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,
    handleCloseSnackbar,
  } = useCustomerList();

  // Table-specific handlers including sort
  const {
    anchorEl,
    menuCustomerId,
    handleMenuClose,
    handlePrevious,
    handleNext,
    sortState,
    handleSort,
  } = useCustomerTable({
    page,
    totalPages,
    setPage,
    setSort: setSort as (field: string, order: 'asc' | 'desc') => void,
  });

  // Get customer for menu actions
  const getMenuCustomer = () => customers.find((c: Customer) => c.id === menuCustomerId);

  const handleView = () => {
    const customer = getMenuCustomer();
    if (customer) {
      console.log('View customer:', customer);
      handleMenuClose();
    }
  };

  const handleEdit = () => {
    const customer = getMenuCustomer();
    if (customer) {
      // Note: handleOpenEditModal would be needed from useCustomerList
      // For now, using console.log
      console.log('Edit customer:', customer);
      handleMenuClose();
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3.5 }, px: { xs: 2, sm: 3 } }}>
      {/* Page Header */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            color: '#111827',
            mb: 0.5,
          }}
        >
          Customers
        </Typography>
      </Box>

      {/* Stats Dashboard */}
      <CustomerStats />

      {/* Customer List Section */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            color: '#111827',
            mb: 0.25,
          }}
        >
          Customers List
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#6b7280',
            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
          }}
        >
          Total {total}k
        </Typography>
      </Box>

      {/* Search and Actions Header */}
      <CustomerListHeader
        search={search}
        onSearchChange={setSearch}
        onAddCustomer={handleOpenAddModal}
      />

      {/* Customer Table with Sortable Headers */}
      <CustomerTable
        customers={customers}
        isLoading={isLoading}
        isError={isError}
        error={error}
        sortState={sortState}
        onRowClick={handleRowClick}
        onDeleteClick={handleDeleteClick}
        onAddCustomer={handleOpenAddModal}
        onSort={handleSort}
      />

      {/* Pagination */}
      <CustomerPagination
        page={page}
        totalPages={totalPages}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleView}>View</MenuItem>
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleMenuClose}>Delete</MenuItem>
      </Menu>

      {/* Customer Modal */}
      <CustomerModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        customer={selectedCustomer}
        isSubmitting={isCreating || isUpdating}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteCustomerDialog
        open={deleteModalOpen}
        customer={customerToDelete}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CustomerList;
